import { useCallback } from 'react';

import {
  ApiFrontendConfigurationRetrieve200,
  useApiFrontendConfigurationRetrieve,
  useApiI18nSetLanguageCreate,
} from '../../../apiClient/latest';
import type { ApiFrontendConfigurationRetrieveParams } from '../../../apiClient/latest/model';
import { FRONTEND_CONFIGURATION_FLAGS } from '../../../public-configs';
import { Logger } from '../../Logging';
import { ErrorType } from '../../Network/orvalHTTPInterface';

export type SetLanguage = (data: { language: string }) => void;

// convenience export with smaller varname
export const FLAGS = FRONTEND_CONFIGURATION_FLAGS;

export type IsFlagActive = (
  flag: typeof FLAGS[keyof typeof FLAGS],
  defaultValue?: boolean
) => boolean;

export type FrontendConfigurationFlagNames = keyof typeof FRONTEND_CONFIGURATION_FLAGS;

export type UseFrontendConfiguration = ReturnType<typeof useApiFrontendConfigurationRetrieve> & {
  data?: ApiFrontendConfigurationRetrieve200; // seems to be required to type this again?
  error: ErrorType<Error> | null;
  isFlagActive: IsFlagActive;
  language?: string;
  settings?: ApiFrontendConfigurationRetrieve200['settings'];
  supportsReferral?: boolean;
  setLanguage: SetLanguage;
  branchKey?: string;
  supportsMobileApp?: boolean;
};

export const useFrontendConfiguration = ({
  cacheTime = 5 * 60 * 1000,
  enabled = true,
  refetchOnMount = false,
  params,
}: Partial<{
  params?: ApiFrontendConfigurationRetrieveParams;
  cacheTime: number;
  enabled: boolean;
  refetchOnMount: boolean | 'always';
}> = {}): UseFrontendConfiguration => {
  const query = useApiFrontendConfigurationRetrieve(params, {
    query: { enabled, refetchOnMount, cacheTime },
  });

  const { mutate } = useApiI18nSetLanguageCreate();

  const isFlagActive: IsFlagActive = (flag, defaultValue = false) => {
    const { flags: hashedFlags } = query.data || {};
    if (!hashedFlags) {
      Logger.warn(
        `No flags found in frontend configuration data, using default value of ${defaultValue}`
      );
      return defaultValue;
    }
    if (!hashedFlags[flag]) {
      // warn about slightly weird legacy behavior where we return false instead of default value
      Logger.warn(
        `Flag ${flag} was not found in the frontend configuration data, this will cause isFlagActive to return false instead of using default value of ${defaultValue}`
      );
    }
    return !!hashedFlags[flag];
  };

  const settings = query.data?.settings;
  const language = settings?.language;
  const supportsReferral = settings?.supportsReferral;
  const branchKey = settings?.branchKey;
  const supportsMobileApp = settings?.supportsMobileApp;

  const setLanguage: SetLanguage = useCallback((data) => {
    mutate({ data });
  }, []);

  return {
    ...query,
    isFlagActive,
    settings,
    language,
    supportsReferral,
    setLanguage,
    branchKey,
    supportsMobileApp,
  };
};
